import { useBrandhubPressReleasesInfiniteQuery } from '@brandhub/hooks/brandhubQueries';
import { useCurrentBrandHubDomainId } from '@brandhub/hooks/useCurrentBrandHubDomainId';
import { PressReleasesItem } from '@brandhub/types/brandhub';
import { ErrorState } from '@components/ErrorState';
import CheckM from '@semcore/icon/Check/m';
import Spin from '@semcore/ui/spin';
import { Text } from '@semcore/ui/typography';
import { useHasMultipleBrandsAccess } from '@src/hooks/useHasMultipleBrandsAccess';
import { useInfiniteScroll } from '@src/hooks/useInfiniteScroll';
import { cn } from '@src/utils/cn';
import { formatDate } from '@src/utils/date';
import { useTranslation } from 'react-i18next';

interface SelectPressReleaseModalContentProps {
  selectedPressRelease: PressReleasesItem | null;
  onSelectedPressReleaseChange: (pressRelease: PressReleasesItem) => void;
}

export const SelectPressReleaseModalContent = ({
  selectedPressRelease,
  onSelectedPressReleaseChange,
}: SelectPressReleaseModalContentProps) => {
  const { t } = useTranslation(['common']);

  const hasMultipleBrandsAccess = useHasMultipleBrandsAccess();
  const [brandHubDomainId] = useCurrentBrandHubDomainId();

  const { data, isLoading, isFetching, isError, hasNextPage, refetch, fetchNextPage } =
    useBrandhubPressReleasesInfiniteQuery({
      ...(hasMultipleBrandsAccess && { brandHubDomainId }),
    });

  const pressReleases = data?.pages.flatMap((page) => page.pressReleases) ?? [];

  const loaderRef = useInfiniteScroll(fetchNextPage, isFetching);

  if (isLoading) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <Spin size="xl" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex-1">
        <ErrorState
          reloadMessage={t('common:reload')}
          onReload={refetch}
        />
      </div>
    );
  }

  return (
    <div className="bg-primary-neutral-hover border-secondary relative flex flex-1 flex-col gap-2.5 overflow-auto rounded-md border p-4">
      {pressReleases.map((pressRelease) => (
        <label
          key={pressRelease.id}
          className={cn(
            'border-primary hover:border-info-active flex cursor-pointer flex-col gap-1 rounded-md border bg-white p-4 transition-colors',
            {
              'bg-secondary-info border-info': selectedPressRelease?.id === pressRelease.id,
            },
          )}>
          <div className="flex items-start justify-between gap-2">
            <Text
              bold
              size={200}>
              {pressRelease.title}
            </Text>
            <div className="flex size-4">
              {selectedPressRelease?.id === pressRelease.id && <CheckM color="icon-primary-info" />}
            </div>
          </div>
          <Text
            color="text-secondary"
            size={200}>
            {formatDate(pressRelease.createdAt)}
          </Text>
          <input
            checked={selectedPressRelease?.id === pressRelease.id}
            className="sr-only"
            data-test="select-press-release-radio"
            type="radio"
            value={pressRelease.id}
            onChange={() => {
              onSelectedPressReleaseChange(pressRelease);
            }}
            onFocus={(event) => {
              event.preventDefault();
            }}
          />
        </label>
      ))}
      {hasNextPage && (
        <div
          ref={loaderRef}
          className="flex justify-center py-8"
          data-test="journalist-articles-list-spinner">
          <Spin size="l" />
        </div>
      )}
    </div>
  );
};
